/**
 * Builds the payload handed to the native share sheet (`Share.share` /
 * `navigator.share`).
 *
 * There is no public Du Chef recipe page yet, so a bare Du Chef link would drop
 * the recipient on a login wall. Until that page exists the *source* URL leads
 * the payload — the recipient gets something they can cook from immediately —
 * and the Du Chef link rides along as a bonus for people who have the app.
 * Flip the order once the public page ships.
 */

export const PAPRIKA_RECIPE_BASE_URL = 'https://duchef.app/recipe-detail';

export interface ShareableRecipe {
  id: string | number;
  title: string;
  sourceUrl?: string;
  /** Total time in minutes. */
  readyInMinutes?: number;
}

export interface RecipeSharePayload {
  title: string;
  message: string;
  /** The link handed to the OS — share targets often prefer this over `message`. */
  url: string;
}

export interface ShareLabels {
  /** e.g. (30) => "30 min" */
  minutes?: (count: number) => string;
  /** e.g. "Dans Du Chef :" */
  inPaprika?: string;
}

export function buildPaprikaRecipeUrl(id: string | number): string {
  return `${PAPRIKA_RECIPE_BASE_URL}/${id}`;
}

export function buildRecipeSharePayload(
  recipe: ShareableRecipe,
  options: { paprikaUrl?: string; labels?: ShareLabels } = {}
): RecipeSharePayload {
  const { labels } = options;
  const minutesLabel = labels?.minutes ?? ((count: number) => `${count} min`);
  const inPaprikaLabel = labels?.inPaprika ?? 'Dans Du Chef :';

  const paprikaUrl = options.paprikaUrl ?? buildPaprikaRecipeUrl(recipe.id);
  const sourceUrl = recipe.sourceUrl?.trim() || undefined;

  const hasTime = typeof recipe.readyInMinutes === 'number' && recipe.readyInMinutes > 0;
  const headline = hasTime
    ? `${recipe.title} — ${minutesLabel(recipe.readyInMinutes!)}`
    : recipe.title;

  // With a source: source link first, Du Chef link as a trailing bonus.
  // Without one: the Du Chef link is the whole payload and stands alone — no
  // dangling "Dans Du Chef :" prefix.
  const lines = sourceUrl
    ? [headline, '', sourceUrl, '', `${inPaprikaLabel} ${paprikaUrl}`]
    : [headline, '', paprikaUrl];

  return {
    title: recipe.title,
    message: lines.join('\n'),
    url: sourceUrl ?? paprikaUrl,
  };
}

export default buildRecipeSharePayload;
